/** Bloc de contribution (patrimoine / archives). */
import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { sanitize } from "../../lib/sanitize";

export function ContributionFormBlock({ data, vars: _vars }) {
  const qs = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [state, setState] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    period: "", subject: qs.get("sujet") || "", message: qs.get("message") || "",
    allow_contact: true, allow_publish: false,
  });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await api.post("/public/contributions", state);
      setStatus("sent");
      toast.success("Merci — votre témoignage a été transmis à l'équipe.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue.";
      toast.error(typeof msg === "string" ? msg : "Erreur");
      setStatus("idle");
    }
  };

  if (status === "sent") {
    return (
      <section className="py-16">
        <div className="border border-pierre bg-white p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-domaine">Merci pour votre contribution</h2>
          <p className="text-ardoise/80 mt-3 max-w-xl mx-auto">
            Votre témoignage a bien été transmis à l&apos;équipe du Domaine. Nous vous recontacterons
            si nous avons besoin de précisions ou pour vous informer de la suite donnée.
          </p>
        </div>
      </section>
    );
  }

  const inp = "w-full px-3 py-2 border border-pierre bg-white text-sm text-ardoise focus:outline-none focus:border-or";
  const setF = (k, v) => setState((s) => ({ ...s, [k]: v }));
  return (
    <section className="py-16" data-testid="contribution-form">
      <div className="border border-pierre bg-white p-8 md:p-12">
        {data.eyebrow && <div className="text-[11px] uppercase tracking-[0.3em] text-or mb-3">{data.eyebrow}</div>}
        {data.title && <h2 className="text-2xl md:text-3xl font-serif text-domaine">{data.title}</h2>}
        {data.description && <p className="text-ardoise/80 mt-3 max-w-3xl" dangerouslySetInnerHTML={{ __html: sanitize(data.description) }} />}
        {(data.wanted_items && data.wanted_items.length > 0) && (
          <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-1.5 text-sm text-ardoise/80 max-w-3xl">
            {data.wanted_items.map((w, i) => (
              <li key={`w-${i}`} className="flex gap-2"><span className="text-or">•</span>{w}</li>
            ))}
          </ul>
        )}
        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          <label className="block text-xs uppercase tracking-widest text-ardoise/60">
            <span className="block mb-1">Prénom *</span>
            <input required value={state.first_name} onChange={(e) => setF("first_name", e.target.value)} className={inp} data-testid="contrib-first-name" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-ardoise/60">
            <span className="block mb-1">Nom *</span>
            <input required value={state.last_name} onChange={(e) => setF("last_name", e.target.value)} className={inp} data-testid="contrib-last-name" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-ardoise/60">
            <span className="block mb-1">Email *</span>
            <input required type="email" value={state.email} onChange={(e) => setF("email", e.target.value)} className={inp} data-testid="contrib-email" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-ardoise/60">
            <span className="block mb-1">Téléphone (facultatif)</span>
            <input value={state.phone} onChange={(e) => setF("phone", e.target.value)} className={inp} />
          </label>
          <label className="block text-xs uppercase tracking-widest text-ardoise/60">
            <span className="block mb-1">Période concernée</span>
            <input value={state.period} onChange={(e) => setF("period", e.target.value)} className={inp} placeholder="Ex : XIXe siècle, 1944, années 1950…" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-ardoise/60">
            <span className="block mb-1">Sujet</span>
            <input value={state.subject} onChange={(e) => setF("subject", e.target.value)} className={inp} placeholder="Ex : Le Chai, Ernest Papineau…" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-ardoise/60 md:col-span-2">
            <span className="block mb-1">Votre témoignage *</span>
            <textarea required rows={6} value={state.message} onChange={(e) => setF("message", e.target.value)} className={inp} data-testid="contrib-message" />
          </label>
          <div className="md:col-span-2 space-y-2 text-sm text-ardoise/80">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={state.allow_contact} onChange={(e) => setF("allow_contact", e.target.checked)} className="mt-0.5" />
              <span>J&apos;autorise l&apos;équipe du Domaine à me recontacter pour affiner mon témoignage.</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={state.allow_publish} onChange={(e) => setF("allow_publish", e.target.checked)} className="mt-0.5" />
              <span>J&apos;autorise la publication éventuelle de ma contribution (avec relecture préalable).</span>
            </label>
          </div>
          <p className="md:col-span-2 text-[11px] text-ardoise/50">
            Votre contribution ne sera jamais publiée automatiquement — elle sera relue avant toute mise en ligne.
          </p>
          <button type="submit" disabled={status === "sending"}
                  data-testid="contrib-submit"
                  className="md:col-span-2 inline-flex items-center justify-center px-8 py-3 bg-domaine text-lin text-xs uppercase tracking-[0.2em] hover:bg-or transition-colors disabled:opacity-50">
            {status === "sending" ? "Envoi…" : (data.cta_label || "Contribuer à l'histoire de la Briouse")}
          </button>
        </form>
      </div>
    </section>
  );
}
